import { computed, defineComponent, toRaw } from "vue";
import { Container } from "aurelia-dependency-injection";
import GoogleMaps = google.maps;
import moment from "moment";

import { INearbyEmpPanelList, INearbyEmpPinsListRes } from "../../../resources-vue/vue-interfaces/i-visit-map";
import { UserService } from "../../../services/user-service";
import { ITypeaheadOptions } from "../../../resources-vue/vue-interfaces/i-typeahead";
import { ToastrService } from "../../../services/toastr.service";
import { CalendarMapService } from "../../../services-vue/calendar-map-service";
import { formatFullAddress, formatReferralFullAddress } from "../../../common/vue-helpers/modifiers/value-modifier";
import { ReferralService } from "../../../services/referral-service";
import { PatientService } from "../../../services/patient.service";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../enums/PermissionActionEnum";
import {
    getApplicationId,
    getProductFullName,
    getProductPillClass
} from "../../../common/vue-helpers/modifiers/application-modifiers";
import { getSVGIcon } from "../../CentralCalendar/VisitMapSVGs";
import { IAddCalendarMarkerOptions } from "../../../resources-vue/vue-interfaces/i-calendar-map";
import { AnalyticsService } from "../../../services/analytics.service";
import { ApplicationEnum } from "../../../enums/ApplicationEnum";
import { IAssignClinician, IAssignClinicianResponse } from "../../../interfaces/i-get-referral";
import { HospiceReferral } from "../../../models/hospice-referral";
import { IPromptOptions } from "../../../resources-vue/vue-interfaces/i-prompt";
import { adjustDate } from "../../../common/adjust-date";

import MultiSelectFilter from "../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import ProductPill from "../../../resources-vue/vue-elements/ProductPill/ProductPill.vue";
import Prompt from "../../../resources-vue/vue-dialogs/prompt/prompt.vue";

export default defineComponent({
    components: {
        MultiSelectFilter,
        ProductPill
    },
    data(vm) {
        return {
            _referralService: null as ReferralService,
            _analyticsService: null as AnalyticsService,
            _permissionManager: null as PermissionManager,
            _patientService: null as PatientService,
            _calendarMapService: null as CalendarMapService,
            _toastrService: null as ToastrService,
            _userService: null as UserService,
            _map: null as GoogleMaps.Map,
            _patientMarker: null as GoogleMaps.Marker,
            _bounds: null as GoogleMaps.LatLngBounds,
            nearbyEmpSearchTerm: "" as string,
            timeout: null,
            pageCount: 0 as number,
            totalPageCount: 1 as number,
            totalItemCount: 0 as number,
            showLoading: true as boolean,
            selectedNearbyEmp: {} as INearbyEmpPinsListRes,
            nearbyEmpPinsList: [] as INearbyEmpPinsListRes[],
            nearbyEmpPanelList: [] as any,
            nearbyEmpDistance: 15 as number,
            credentialsList: [] as string[],
            selectedCredentials: [] as ITypeaheadOptions[],
            userOs: "",
            observer: null,
            referralDetails: null as any,
            isReassignAllowed: false as boolean,
            todaysDate: new Date(Date.now()) as Date,
            dialogRef: null,
            startDate: null as Date,
            endDate: null as Date,
            weekDayRange: 1 as number,
            getApplicationId
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    async created() {
        this.userOs = window.sessionStorage.getItem("userOs");
        this._userService = Container.instance.get(UserService);
        this._toastrService = Container.instance.get(ToastrService);
        this._calendarMapService = Container.instance.get(CalendarMapService);
        this._patientService = Container.instance.get(PatientService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this._referralService = Container.instance.get(ReferralService);
    },

    async mounted() {
        this.isReassignAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.clinician,
            PermissionActionEnum.canReassign
        );
        this.credentialsList = await this._userService.getUserCredentials();
        if (Number(this.$route.query.application) === ApplicationEnum.AgencyCore) {
            this.referralDetails = await this.getReferralData();
            await this._calendarMapService.getCoordsFromAddress(
                formatFullAddress(this.referralDetails.Demographics.PrimaryAddress),
                (newcoords) => {
                    this.referralDetails.Demographics.PrimaryAddress.latitude = Number(newcoords.lat);
                    this.referralDetails.Demographics.PrimaryAddress.longitude = Number(newcoords.lng);
                    this.markPatientLocation();
                }
            );
        } else {
            this.referralDetails = await this.getHosPalReferralData();
            const address = formatReferralFullAddress(this.referralDetails.primaryAddress);
            if (address && address.length > 3) {
                await this._calendarMapService.getCoordsFromAddress(address, (newcoords) => {
                    this.referralDetails.primaryAddress.latitude = Number(newcoords.lat);
                    this.referralDetails.primaryAddress.longitude = Number(newcoords.lng);
                    this.markPatientLocation();
                });
            }
        }
        this.getWeekRangeDates();
        setTimeout(async () => {
            this.observer = new IntersectionObserver(
                (entries: any) => {
                    if (entries[0].isIntersecting) this.loadMoreEmployeesList();
                },
                { root: null, threshold: 0 }
            );
            this.observer.observe(this.$refs.trigger);
            await this.getNearbyEmpList();
        }, 2000);
    },

    watch: {
        nearbyEmpSearchTerm: async function () {
            await this.getNearbyEmpList();
        },
        nearbyEmpDistance: async function () {
            await this.getNearbyEmpList();
        },
        selectedCredentials: async function () {
            await this.getNearbyEmpList();
            // fix: watcher doesn't work for clear selected option
        }
    },

    computed: {
        isHomeHealth() {
            return Number(this.$route.query.application) === ApplicationEnum.AgencyCore;
        }
    },

    methods: {
        getWeekRangeDates() {
            const currentDate = new Date(this.todaysDate);
            let dayRequested = this.weekDayRange - 1;
            if (dayRequested < 0) {
                dayRequested = 0;
            }
            let offset = dayRequested - currentDate.getDay();
            if (offset > 0) {
                offset -= 7;
            }
            this.startDate = adjustDate(currentDate, offset);
            this.endDate = adjustDate(this.startDate, 6);
        },
        userCredentialsFetch(filter: string) {
            return this.credentialsList
                .map((credential, index) => ({
                    name: credential,
                    value: index.toString()
                }))
                .filter((x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
        },

        selectedItemsUpdated(options: any) {
            this.selectedCredentials = options;
        },

        async getNearbyEmpList() {
            if (this.timeout) clearTimeout(this.timeout);

            this.timeout = setTimeout(async () => {
                this.pageCount = 0;
                this.totalPageCount = 1;
                this.totalItemCount = 0;
                this.showLoading = true;
                this.selectedNearbyEmp = {} as INearbyEmpPinsListRes;
                // remove previous markers
                if (this.nearbyEmpPinsList.length) {
                    const pins = [...this.nearbyEmpPinsList];
                    this.clearAllNearbyEmpMarkers(pins);
                }
                this.nearbyEmpPanelList = [];
                try {
                    const pinsListPayload: any = {
                        Term: this.nearbyEmpSearchTerm,
                        Distance: Number(this.nearbyEmpDistance),
                        AgencyId: this.$route.query.accountId,
                        Application: Number(this.$route.query.application),
                        LocationId: this.$route.query.branchId,
                        Credentials: this.selectedCredentials?.map((credential) => `;${credential.name};`),
                        Latitude:
                            Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                                ? this.referralDetails.Demographics.PrimaryAddress.latitude
                                : this.referralDetails.primaryAddress.latitude,
                        Longitude:
                            Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                                ? this.referralDetails.Demographics.PrimaryAddress.longitude
                                : this.referralDetails.primaryAddress.longitude
                    };
                    this.nearbyEmpPinsList = await this._userService.getNearByClinicians(pinsListPayload);
                    this.markNearbyEmpPins(this.nearbyEmpPinsList);
                } catch (error) {
                    this._toastrService.error({
                        title: "Error",
                        message: "There was a problem while getting Nearby Employees. Please try again."
                    });
                }
            }, 1200);
        },

        async loadMoreEmployeesList() {
            if (this.pageCount < this.totalPageCount) {
                try {
                    const panelListPayload: INearbyEmpPanelList = {
                        Term: this.nearbyEmpSearchTerm,
                        Distance: Number(this.nearbyEmpDistance),
                        AgencyId: this.$route.query.accountId as string,
                        Application: Number(this.$route.query.application),
                        LocationId: this.$route.query.branchId as string,
                        Credentials: this.selectedCredentials?.map((credential) => `;${credential.name};`),
                        StartDate: moment(this.startDate).format("MM/DD/YYYY"),
                        EndDate: moment(this.endDate).format("MM/DD/YYYY"),
                        CurrentDate: this.todaysDate as any,
                        Latitude:
                            Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                                ? this.referralDetails.Demographics.PrimaryAddress.latitude
                                : this.referralDetails.primaryAddress.latitude,
                        Longitude:
                            Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                                ? this.referralDetails.Demographics.PrimaryAddress.longitude
                                : this.referralDetails.primaryAddress.longitude,
                        SelectedUser: this.selectedNearbyEmp.id,
                        Page: this.pageCount + 1,
                        PageLength: 50
                    };
                    const response = await this._userService.getNearByEmpPanelList(panelListPayload);
                    this.totalPageCount = response.PageCount;
                    this.totalItemCount += response.ItemCount;
                    this.pageCount++;
                    // Temporary fix: BE sends null if the selected user is not present in the filter criteria
                    const items = response.Items[0] === null ? response.Items.splice(1) : response.Items;
                    this.nearbyEmpPanelList = [...this.nearbyEmpPanelList, ...items];
                    this.showLoading = !(this.nearbyEmpPanelList.length === 0);
                } catch (error) {
                    this._toastrService.error({
                        title: "Error",
                        message: "There was a problem while getting Nearby Employees. Please try again."
                    });
                }
            } else {
                this.showLoading = false;
            }
        },

        async getReferralData() {
            try {
                const result = await this._patientService.getEditHHReferral({
                    referralId: this.$route.query.referralId as string,
                    agencyId: this.$route.query.accountId as string,
                    application: Number(this.$route.query.application)
                });
                if (result) {
                    let tempReferral = result.data;
                    tempReferral.AgencyId = this.$route.query.accountId;
                    return tempReferral;
                }
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while getting this referral. Please try again. If the problem persists, please report it.`
                });
            }
        },
        async getHosPalReferralData() {
            const queryParam = {
                id: this.$route.query.referralId as string,
                accountId: this.$route.query.accountId as string,
                application: Number(this.$route.query.application)
            };
            let referral = (await this._referralService.getReferral(
                this.$route.query.referralId as string,
                queryParam
            )) as HospiceReferral;
            return referral;
        },
        markNearbyEmpPins(employeeList: any, selectedId?: string) {
            for (let [index, emp] of employeeList.entries()) {
                const coords = {
                    lat: Number(emp.latitude),
                    lng: Number(emp.longitude)
                };
                let marker = null;
                const credentials = emp.credentials.split(",")[0];
                // used to grey out all the pins except the selected employee pin
                const isCurrentEmpSelected = selectedId === emp.id;
                const useGreyPinColor = selectedId ? !isCurrentEmpSelected : false;
                const icon = {
                    url:
                        "data:image/svg+xml;charset=UTF-8," +
                        encodeURIComponent(
                            useGreyPinColor ? getSVGIcon(credentials, "#707070") : getSVGIcon(credentials)
                        ),
                    scaledSize: isCurrentEmpSelected ? new google.maps.Size(55, 55) : new google.maps.Size(50, 50)
                };
                if (!!coords.lat && !!coords.lng) {
                    try {
                        const options: IAddCalendarMarkerOptions = {
                            position: coords,
                            map: this._map,
                            icon: icon
                        };
                        marker = this._calendarMapService.addMarker(options);
                        this._analyticsService.logEvent({
                            category: "Create-Marker",
                            action: "Coordinates"
                        });
                        employeeList[index].marker = marker;
                        this.initNearbyEmpMarkerEvent(employeeList[index]);
                    } catch (error) {
                        console.error(error);
                    }
                }
            }
        },
        initNearbyEmpMarkerEvent(employee: any) {
            if (employee.marker) {
                employee.marker.addListener("click", async () => {
                    // grey out rest of the pins
                    this.selectedNearbyEmp = employee;
                    this.clearAllNearbyEmpMarkers(this.nearbyEmpPinsList);
                    this.pageCount = 0;
                    this.totalPageCount = 1;
                    this.totalItemCount = 0;
                    this.nearbyEmpPanelList = [];
                    this.markNearbyEmpPins(this.nearbyEmpPinsList, this.selectedNearbyEmp.id);
                });
            }
        },
        clearAllNearbyEmpMarkers(locations: any) {
            for (let [index, emp] of locations.entries()) {
                let currentMarker = locations[index].marker;
                if (currentMarker) {
                    toRaw(currentMarker).setMap(null);
                }
            }
        },
        markPatientLocation() {
            try {
                const lat: number =
                    Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                        ? this.referralDetails.Demographics.PrimaryAddress.latitude
                        : this.referralDetails.primaryAddress.latitude;
                const lon: number =
                    Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                        ? this.referralDetails.Demographics.PrimaryAddress.longitude
                        : this.referralDetails.primaryAddress.longitude;
                this._bounds = this._calendarMapService.getBounds();
                const image = require(`../../../assets/images/client-pin.svg`);
                this._map = this._calendarMapService.getMap(document.getElementById("NearbyEmp__VisitMap"), {
                    center: {
                        lat: lat,
                        lng: lon
                    },
                    zoom: 18, // Higher is zoom in and lower is zoom out
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    gestureHandling: "greedy"
                });
                this._patientMarker = this._calendarMapService.addMarker({
                    position: {
                        lat: lat,
                        lng: lon
                    },
                    map: this._map,
                    icon: image
                });

                const info = this._calendarMapService.addInfoWindow({
                    content: this.getPatientInfoWindowContent(this.referralDetails)
                });
                // Marker Events
                this._patientMarker.addListener("mouseover", () => {
                    info.open(this._map, this._patientMarker);
                });
                this._patientMarker.addListener("mouseout", () => {
                    info.close();
                });

                this._bounds.extend(this._patientMarker.getPosition());
            } catch (error) {
                console.error(error?.message);
            }
        },

        getPatientInfoWindowContent(referralDetails: any): string {
            let tempApplicationId: ApplicationEnum;
            if (Number(this.$route.query.application) === ApplicationEnum.AxxessHospiceFE) {
                tempApplicationId = ApplicationEnum.AxxessHospice;
            } else if (Number(this.$route.query.application) === ApplicationEnum.AxxessPalliative) {
                tempApplicationId = ApplicationEnum.AxxessPalliative;
            } else {
                tempApplicationId = Number(this.$route.query.application);
            }

            const firstName =
                Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                    ? this.referralDetails.Demographics.FirstName
                    : this.referralDetails.firstName;
            const lastName =
                Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                    ? this.referralDetails.Demographics.LastName
                    : this.referralDetails.lastName;
            const primaryAddress =
                Number(this.$route.query.application) === ApplicationEnum.AgencyCore
                    ? formatFullAddress(this.referralDetails.Demographics.PrimaryAddress)
                    : formatReferralFullAddress(this.referralDetails.primaryAddress);
            return ` <div id="${referralDetails.id}" class="visit-marker-details">
                        <span class="${getProductPillClass(tempApplicationId)} pill">
                            ${getProductFullName(tempApplicationId)}
                        </span>
                        <big class="info-header"><b>${firstName} ${lastName}</b></big>
                        <div class="visit-address">${primaryAddress}</div>
                        </div>`;
        },
        highlightEmpPin(empId: string) {
            this.clearAllNearbyEmpMarkers(this.nearbyEmpPinsList);
            this.markNearbyEmpPins(this.nearbyEmpPinsList, empId);
        },

        async clearNearbyEmpFilters() {
            // reset all the filters
            this.nearbyEmpSearchTerm = "";
            this.nearbyEmpDistance = 15;
            (this.$refs.credentialsRef as any).toggleSelection();
            this.pageCount = 0;
            this.totalPageCount = 1;
            this.totalItemCount = 0;
            await this.getNearbyEmpList();
        },

        handleDone() {
            this.$router.back();
        },
        async handleAssignReferral(emp: any) {
            const payload = {
                ReferralId: this.$route.query.referralId as string,
                UserId: emp.UserDetails.sourceUserId,
                AgencyId: this.$route.query.accountId as string,
                Application: Number(this.$route.query.application)
            } as IAssignClinician;

            let promptOptions: IPromptOptions = {
                message: "Are you sure that you want to select this employee as the Referral's Assign to Clinician?",
                okText: "Yes",
                cancelText: "No"
            };
            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: async (confirm) => {
                    if (confirm.data === "success") {
                        const response = (await this._referralService.assignClinician(
                            payload
                        )) as IAssignClinicianResponse;
                        if (response.isSuccessful) {
                            this._toastrService.success({
                                title: "Assigned",
                                message: response.message
                            });
                            this.$router.back();
                        } else {
                            this._toastrService.success({
                                title: "Error",
                                message: "Failed to assign clinician"
                            });
                        }
                    }
                }
            });
        }
    }
});
